import { useState, useEffect } from "react";
import styles from "./AllLiveSessionPage.module.css";
import { FiSearch } from "react-icons/fi";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import SessionCard from "../../components/LiveSessionCard/SessionCard";
import useAuthStore from "../../store/authStore";
import useDashboardStore from "../../store/dashboardStore";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
const AllLiveSessions = () => {
  const user = useAuthStore((state) => state.user);
  const { setEditLiveSession, setIsCreateLiveSessionScreen } =
    useDashboardStore();
  const [sessions, setSessions] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Check if user is admin
  const isAdmin = user?.roles[0] === "administrator";

  const handleEdit = (session) => {
    setEditLiveSession(session);
    setIsCreateLiveSessionScreen();
  };

  const handleDelete = (session) => {
    confirmAlert({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this session?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            const { data } = await api.delete(
              ENDPOINTS.DELETE_LIVE_SESSION_BY_ID(session.id)
            );
            if (data.success) {
              fetchSessions();
              toast.success(data.message || "Session deleted successfully");
            }
          },
        },
        {
          label: "No",
          onClick: () => toast.error("Session deletion cancelled"),
        },
      ],
    });
  };

  const fetchSessions = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get(ENDPOINTS.GET_ALL_LIVE_SESSION);
      if (data.success && user?.id) {
        if (isAdmin) {
          setSessions(data.sessions);
        } else {
          const filtered = data.sessions.filter(
            (s) => s.instructor_id === user.id
          );
          setSessions(filtered);
        }
      }
    } catch (err) {
      console.error("Failed to fetch sessions", err);
      toast.error("Failed to fetch live sessions");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchSessions();
    }
  }, [user?.id, user?.roles]);

  const filteredSessions = sessions.filter((session) =>
    session.session_title.toLowerCase().includes(search.toLowerCase())
  );

  // ─── Selection ────────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allFilteredSelected =
    filteredSessions.length > 0 &&
    filteredSessions.every((s) => selectedIds.has(s.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredSessions.forEach((s) => next.delete(s.id));
      } else {
        filteredSessions.forEach((s) => next.add(s.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    confirmAlert({
      title: "Confirm Delete",
      message: `Delete ${ids.length} selected session${ids.length > 1 ? "s" : ""}? This cannot be undone.`,
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const { data } = await api.post(
                ENDPOINTS.BULK_DELETE_LIVE_SESSIONS,
                { session_ids: ids }
              );
              if (data.success) {
                clearSelection();
                fetchSessions();
                toast.success(
                  data.message || `${data.deleted} session(s) deleted`
                );
              } else {
                toast.error(data.message || "Something went wrong");
              }
            } catch (err) {
              console.error("Bulk delete failed", err);
              toast.error("Failed to delete sessions");
            }
          },
        },
        {
          label: "No",
          onClick: () => toast.error("Deletion cancelled"),
        },
      ],
    });
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>Live Sessions</h2>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Type in to search .."
              className={styles.searchInput}
              disabled
            />
            <FiSearch className={styles.searchIcon} />
          </div>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Loading live sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>Live Sessions</h2>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Type in to search .."
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FiSearch className={styles.searchIcon} />
        </div>
      </div>

      {/* Bulk actions toolbar (admins only) */}
      {isAdmin && filteredSessions.length > 0 && (
        <div className={styles.bulkBar}>
          <label className={styles.bulkSelectAll}>
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={toggleSelectAll}
            />
            <span>
              {allFilteredSelected ? "Unselect all" : "Select all"}
              {` (${filteredSessions.length})`}
            </span>
          </label>

          {selectedIds.size > 0 && (
            <div className={styles.bulkActions}>
              <span className={styles.bulkCount}>
                {selectedIds.size} selected
              </span>
              <button className={styles.bulkClear} onClick={clearSelection}>
                Clear
              </button>
              <button
                className={styles.bulkDelete}
                onClick={handleBulkDelete}
              >
                Delete selected
              </button>
            </div>
          )}
        </div>
      )}

      <div className={styles.bodyContainer}>
        {filteredSessions.map((session, i) => (
          <div className={styles.sessionCardBody} key={session.id ?? i}>
            {isAdmin && (
              <input
                type="checkbox"
                className={styles.rowCheckbox}
                checked={selectedIds.has(session.id)}
                onChange={() => toggleSelect(session.id)}
              />
            )}
            <div className={styles.sessionCard}>
              <SessionCard session={session} />
            </div>
            {/* Only show Edit/Delete for admins */}
            {isAdmin && (
              <>
                <span
                  className={styles.edit}
                  onClick={() => handleEdit(session)}
                >
                  Edit
                </span>
                <span className={styles.divider}> - </span>
                <span
                  className={styles.delete}
                  onClick={() => handleDelete(session)}
                >
                  Delete
                </span>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllLiveSessions;
